(function initDiagnostics(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.STIDiagnostics = api;
}(typeof window !== 'undefined' ? window : globalThis, function diagnosticsFactory() {
  'use strict';

  const STORAGE_KEY = 'sti_diagnostics_consent_v1';
  const CONSENT_VERSION = '2026-09-11';
  const CARD_PATCH_IDS = Object.freeze([
    'T-350', 'T-803', 'T-767', 'T-1151', 'T-343', 'T-1495',
    'T-708', 'T-436', 'T-147', 'T-572', 'T-775', 'T-785',
    'T-892', 'T-244', 'T-253', 'T-252', 'T-608', 'T-842',
    'T-630', 'T-517', 'T-510', 'T-620', 'T-751', 'T-551',
  ]);
  const STRING_EXIF_KEYS = Object.freeze([
    'cameraMake', 'cameraModel', 'lensModel', 'whiteBalance', 'lightSource',
    'exposureMode', 'exposureProgram', 'sceneCaptureType', 'customRendered', 'software',
  ]);
  const NUMBER_EXIF_KEYS = Object.freeze([
    'orientation', 'iso', 'exposureTime', 'fNumber', 'focalLength', 'focalLength35mm',
  ]);
  const WARNING_CODES = new Set([
    'exif_missing', 'exif_parse_error', 'exif_unsupported', 'camera_unknown',
    'flash_fired', 'edited_or_shared_copy', 'low_resolution', 'nonstandard_mode',
  ]);

  function getConsent(storage) {
    try {
      const value = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
      return value
        && value.consentVersion === CONSENT_VERSION
        && (value.choice === 'private' || value.choice === 'share')
        ? value.choice
        : null;
    } catch (_error) {
      return null;
    }
  }

  function setConsent(value, storage) {
    if (value !== 'private' && value !== 'share') {
      throw new Error('Invalid diagnostics consent choice.');
    }
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify({ choice: value, consentVersion: CONSENT_VERSION }));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function buildPayload({ metadata, correctionMode, cardResult, patchSamples }) {
    if (correctionMode !== 'white' && correctionMode !== 'card') {
      throw new Error('Invalid correction mode.');
    }
    const exif = {};
    STRING_EXIF_KEYS.forEach(key => {
      if (typeof metadata.exif[key] === 'string') {
        const value = metadata.exif[key]
          .trim()
          .replace(/[\u0000-\u001f\u007f]/g, '')
          .slice(0, 120);
        if (value) exif[key] = value;
      }
    });
    NUMBER_EXIF_KEYS.forEach(key => {
      if (Number.isFinite(metadata.exif[key])) exif[key] = metadata.exif[key];
    });
    if (typeof metadata.exif.flashFired === 'boolean') {
      exif.flashFired = metadata.exif.flashFired;
    }
    const warningCodes = [...new Set(metadata.warningCodes)]
      .filter(code => WARNING_CODES.has(code));
    const card = correctionMode === 'card' ? {
      version: cardResult.version,
      measuredReference: Boolean(cardResult.measured),
      validPatchCount: cardResult.validPatchCount,
      rejectedPatchCount: cardResult.rejectedPatchCount,
      medianFitDeltaE: Number(cardResult.medianDeltaE.toFixed(3)),
      maxFitDeltaE: Number(cardResult.maxDeltaE.toFixed(3)),
      patchSamples: CARD_PATCH_IDS.map((id, index) => {
        const sample = patchSamples[index];
        if (!sample || sample.id !== id) {
          throw new Error('Calibration patch order is invalid.');
        }
        return {
          id,
          r: sample.r,
          g: sample.g,
          b: sample.b,
          clipped: Boolean(sample.clipped),
        };
      }),
    } : null;
    return {
      schemaVersion: 1,
      consentVersion: CONSENT_VERSION,
      exifStatus: ['present', 'missing', 'parse_error', 'unsupported'].includes(metadata.status)
        ? metadata.status
        : 'parse_error',
      exif,
      image: {
        width: Math.round(metadata.image.width),
        height: Math.round(metadata.image.height),
      },
      warningCodes,
      correctionMode,
      card,
    };
  }

  function loadPrivacyApi(shopify) {
    if (shopify && shopify.customerPrivacy) return Promise.resolve(shopify.customerPrivacy);
    if (!shopify || typeof shopify.loadFeatures !== 'function') return Promise.resolve(null);
    return new Promise(resolve => {
      shopify.loadFeatures([
        { name: 'consent-tracking-api', version: '0.1' },
      ], error => {
        resolve(error ? null : shopify.customerPrivacy || null);
      });
    });
  }

  async function submitDiagnostics({
    metadata,
    correctionMode,
    cardResult,
    patchSamples,
    storage,
    shopify,
    fetchImpl,
  }) {
    if (getConsent(storage) !== 'share') return { sent: false, reason: 'private' };
    const privacy = await loadPrivacyApi(shopify);
    let allowed = false;
    try {
      allowed = Boolean(privacy && privacy.analyticsProcessingAllowed());
    } catch (_error) {
      allowed = false;
    }
    if (!allowed) return { sent: false, reason: 'analytics_not_allowed' };

    let payload;
    try {
      payload = buildPayload({ metadata, correctionMode, cardResult, patchSamples });
    } catch (_error) {
      return { sent: false, reason: 'invalid_payload' };
    }
    const body = JSON.stringify(payload);
    if (new TextEncoder().encode(body).byteLength > 32768) {
      return { sent: false, reason: 'payload_too_large' };
    }
    try {
      const response = await fetchImpl('/apps/sti-colour-data/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
      return response.ok
        ? { sent: true, reason: 'stored' }
        : { sent: false, reason: `http_${response.status}` };
    } catch (_error) {
      return { sent: false, reason: 'network_error' };
    }
  }

  return Object.freeze({
    STORAGE_KEY,
    CONSENT_VERSION,
    CARD_PATCH_IDS,
    getConsent,
    setConsent,
    buildPayload,
    loadPrivacyApi,
    submitDiagnostics,
  });
}));
